import { post } from "../../../helper/Api_helper";

import {
  RESUME_PARSER_BASE_URL,
  PARSED_RESUME_URL,
  PROCESS_UPLOAD_FILE,
  VYLABS_BASE_URL,
  CREATE_RUN_SET_ID,
  GET_UNPARSED_RESUMES_COUNT,
  WEB_SOCKET_URL,
} from "../../../helper/url_helper";
import envConfig from "../../../helper/env_config";
const staticMonsterUsaVendorId = envConfig.monsterUsaVendorID;

async function processUploadedFile(
  fileNames,
  runSetId,
  vendorId = null,
  token
) {
  const requestUrl = RESUME_PARSER_BASE_URL + PROCESS_UPLOAD_FILE;
  const requestBodyData = {
    fileNames: fileNames,
    runSetID: runSetId,
    vendorID: staticMonsterUsaVendorId,
  };

  const requestBody = JSON.stringify(requestBodyData);
  try {
    const response = await fetch(requestUrl, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "application/json",
      },
      body: requestBody,
    });
    return response.ok;
  } catch (error) {
    console.error("[Monster Parser] Error processing file:", error);
    return null;
  }
}

export async function parseMonsterResumeHeadless(file, options = {}) {
  const vendorId = options.vendorId || null;
  const signal = options.signal;

  let resumeTextContent = null;

  try {
    if (signal?.aborted) {
      return { error: "Operation aborted before start" };
    }
    const appToken = localStorage.getItem("token");
    if (!appToken) {
      return { error: "No app token found. Please login." };
    }

    resumeTextContent = await file.text().catch((err) => {
      console.error(
        "[Monster Parser] Failed to read file content for fallback:",
        err
      );
      return null;
    });

    let ws;
    let pingInterval;
    let pingTimeout;

    const openWebSocket = () => {
      try {
        const wsProtocol =
          window.location.protocol === "https:" ? "wss://" : "wss://";
        const baseHost = String(WEB_SOCKET_URL)
          .replace(/^wss?:\/\//, "")
          .replace(/^https?:\/\//, "")
          .replace(/^\/+/, "");
        const wsUrl = `${wsProtocol}${baseHost}/ws?token=${encodeURIComponent(
          "Bearer " + appToken
        )}`;

        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          const sendPing = () => {
            if (ws && ws.readyState === WebSocket.OPEN && !signal?.aborted) {
              ws.send("ping");
              if (pingTimeout) clearTimeout(pingTimeout);
              pingTimeout = setTimeout(() => {
                try {
                  ws.close(1000, "Ping timeout");
                } catch (e) {}
              }, 10000);
            }
          };
          sendPing();
          pingInterval = setInterval(() => {
            if (!signal?.aborted) {
              sendPing();
            } else {
              clearInterval(pingInterval);
            }
          }, 30000);
        };

        ws.onmessage = (evt) => {
          if (evt.data === "pong") {
            if (pingTimeout) {
              clearTimeout(pingTimeout);
              pingTimeout = null;
            }
          }
        };

        ws.onclose = () => {
          if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = null;
          }
          if (pingTimeout) {
            clearTimeout(pingTimeout);
            pingTimeout = null;
          }
        };

        ws.onerror = (e) => {
          console.error("WebSocket error:", e);
        };
      } catch (e) {
        console.warn("[MonsterHeadlessParser] Failed to open WS", e);
      }
    };

    const closeWebSocket = () => {
      try {
        if (ws) ws.close();
      } catch (e) {}
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      if (pingTimeout) {
        clearTimeout(pingTimeout);
        pingTimeout = null;
      }
    };

    openWebSocket();

    if (signal) {
      signal.addEventListener("abort", () => {
        closeWebSocket();
      });
    }

    if (signal?.aborted) return { error: "Operation aborted" };

    const runSetId = await post(VYLABS_BASE_URL, CREATE_RUN_SET_ID, {
      folderName: file?.name || "monster-import",
      totalFiles: 1,
    });

    if (!runSetId) {
      closeWebSocket();
      return {
        error: "Failed to create run set",
        fileContent: resumeTextContent,
      };
    }

    if (signal?.aborted) {
      closeWebSocket();
      return { error: "Operation aborted" };
    }

    const requestBody = JSON.stringify({ FileNames: [file.name] });
    const presignedResp = await fetch(
      RESUME_PARSER_BASE_URL + PARSED_RESUME_URL,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + appToken,
          "Content-Type": "application/json",
        },
        body: requestBody,
      }
    );

    if (!presignedResp.ok) {
      closeWebSocket();
      return {
        error: "Failed to get presigned URL",
        fileContent: resumeTextContent,
      };
    }

    const presignedData = await presignedResp.json();
    const presignedUrl = presignedData?.PresignedUploadURLs?.[0];

    if (!presignedUrl) {
      closeWebSocket();
      return {
        error: "No presigned URL returned",
        fileContent: resumeTextContent,
      };
    }
    const uploadResp = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": "application/octet-stream" },
      mode: "cors",
    });

    if (!uploadResp.ok) {
      closeWebSocket();
      return {
        error: "Failed to upload to presigned URL",
        fileContent: resumeTextContent,
      };
    }

    let lastFailReason = null;
    let finalResult = null;
    const MAX_ATTEMPTS = 3;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (signal?.aborted) {
        closeWebSocket();
        return { error: "Operation aborted during polling" };
      }

      try {
        if (attempt === 0) {
          const processResp = await processUploadedFile(
            [file.name],
            runSetId,
            vendorId,
            appToken
          );

          if (!processResp) {
            closeWebSocket();
            return {
              error: "Failed to start processing via processUploadedFile",
              fileContent: resumeTextContent,
            };
          }

          await new Promise((r) => setTimeout(r, 20000));
        }
        const status = await post(VYLABS_BASE_URL, GET_UNPARSED_RESUMES_COUNT, {
          runSetID: runSetId,
        });
        const completed = status?.completed_files || [];
        const failed = status?.failed_files || [];
        const incomplete = status?.incomplete_files || [];

        const found = completed.find(
          (f) => f?.name === file.name && f?.candidate_id
        );
        if (found?.candidate_id) {
          finalResult = {
            candidateId: found.candidate_id,
            fileID: found.id || found.file_id || found.fileID,
          };
          break;
        }

        const incompleteFile = incomplete.find((f) => f?.name === file.name);
        if (incompleteFile?.id) {
          finalResult = {
            status: "INCOMPLETE",
            candidateId: null,
            fileID: incompleteFile.id,
            fileContent: resumeTextContent,
          };
          break;
        }

        const failedFile = failed.find((f) => f?.name === file.name);
        if (failedFile) {
          if (failedFile.id) {
            finalResult = {
              status: "FAILED",
              candidateId: null,
              fileID: failedFile.id,
              fileContent: resumeTextContent,
            };
          }
          lastFailReason = failedFile?.message || "parse_failed";
        }
      } catch (e) {
        console.warn(`Status check attempt ${attempt + 1} failed:`, e);
        lastFailReason = e.message || "Status check failed";
      }

      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, 20000));
      }
    }

    closeWebSocket();

    if (finalResult) {
      return finalResult;
    }

    return {
      error: lastFailReason || "Parsing timeout without candidate_id",
      isDefinitiveFailure: true,
      fileContent: resumeTextContent,
    };
  } catch (err) {
    if (err.name === "AbortError" || signal?.aborted) {
      return { error: "Operation aborted" };
    }
    return {
      error: err?.message || "Unknown headless parse error",
      isDefinitiveFailure: true,
      fileContent: resumeTextContent,
    };
  }
}
